import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    Stack, Grid, Typography, Chip, Box, Dialog, DialogContent, DialogTitle,
    DialogContentText, Button, DialogActions, Alert, IconButton, Snackbar,
    Popper, RadioGroup, FormControlLabel, Radio, Divider, Skeleton, CircularProgress
} from '@mui/material';
import Properties from '../record/components/Properties';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import api from '../Services/Api';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import FilterAltOutlined from '@mui/icons-material/FilterAltOutlined';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ViewListIcon from '@mui/icons-material/ViewList';
import SearchComponent from '../components/SearchComponent';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import InboxIcon from '@mui/icons-material/Inbox';
import MailOutlined from '@mui/icons-material/MailOutlined';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import WorkOutlineOutlined from '@mui/icons-material/WorkOutlineOutlined';
import { getPropertiesContact, getContactName } from './contacts.js';
import { useTranslation } from 'react-i18next';
import {
    glassCard, ACCENT, ACCENT_DARK, ACCENT_GLOW,
    TEXT_PRIMARY, TEXT_SECONDARY, TEXT_TERTIARY, TEXT_HINT,
    SURFACE_BORDER, SURFACE_HOVER,
    STATUS_LOST, STATUS_WON,
    TYPE_SCALE, noMotion, DIALOG_PAPER
} from '../designsystem/tokens';

dayjs.extend(relativeTime);

const currencyFormatter = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'EUR' });

const getCloseDateUrgency = (closedate) => {
    if (!closedate) return { color: TEXT_TERTIARY, urgency: 'none' };
    const daysUntil = dayjs(closedate).diff(dayjs(), 'day');
    if (daysUntil < 0) return { color: STATUS_LOST, urgency: 'overdue' };
    if (daysUntil <= 7) return { color: '#FFB74D', urgency: 'soon' };
    if (daysUntil <= 30) return { color: TEXT_SECONDARY, urgency: 'upcoming' };
    return { color: TEXT_TERTIARY, urgency: 'future' };
};

const getDealAmountWeight = (amount) => {
    if (!amount || amount <= 0) return { fontSize: TYPE_SCALE.xl, fontWeight: 700 };
    if (amount >= 100000) return { fontSize: '1.35rem', fontWeight: 800 };
    if (amount >= 50000) return { fontSize: '1.25rem', fontWeight: 700 };
    return { fontSize: TYPE_SCALE.xl, fontWeight: 700 };
};

const DealCardSkeleton = () => (
    <Box sx={{ ...glassCard }}>
        <Stack spacing={1.5}>
            <Skeleton variant="text" width="65%" sx={{ bgcolor: SURFACE_HOVER, fontSize: TYPE_SCALE.md }} />
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Skeleton variant="text" width="35%" sx={{ bgcolor: SURFACE_HOVER, fontSize: TYPE_SCALE.xl }} />
                <Skeleton variant="text" width="22%" sx={{ bgcolor: SURFACE_HOVER, fontSize: TYPE_SCALE.xs }} />
            </Stack>
            <Skeleton variant="text" width="40%" sx={{ bgcolor: SURFACE_HOVER, fontSize: TYPE_SCALE.xs }} />
            <Stack direction="row" justifyContent="flex-end">
                <Skeleton variant="rounded" width={110} height={44} sx={{ borderRadius: '22px', bgcolor: SURFACE_HOVER }} />
            </Stack>
        </Stack>
    </Box>
);

const StageChipsSkeleton = () => (
    <Stack direction="row" spacing={1} sx={{ py: 1.5 }}>
        {[120, 90, 110, 80, 100].map((w, i) => (
            <Skeleton key={i} variant="rounded" width={w} height={44} sx={{ borderRadius: '22px', bgcolor: SURFACE_HOVER, flexShrink: 0 }} />
        ))}
    </Stack>
);


const MoveDealDialog = ({ open, onClose, deal, stages, pipelineId, onSuccess }) => {
    const { t } = useTranslation();
    const [selectedStage, setSelectedStage] = useState(deal.stage?.id ?? null);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [properties, setProperties] = useState([]);
    const [propsLoading, setPropsLoading] = useState(false);
    const [propsValid, setPropsValid] = useState(true);

    useEffect(() => {
        if (open) {
            setSelectedStage(deal.stage?.id ?? null);
            setError(null);
            setProperties([]);
            setPropsValid(true);
        }
    }, [open, deal.stage?.id]);

    useEffect(() => {
        if (!open || !selectedStage || selectedStage === deal.stage?.id) {
            setProperties([]);
            setPropsValid(true);
            return;
        }
        setPropsLoading(true);
        api.fetchProperties('deal', true, false, pipelineId, selectedStage).then((props) => {
            setProperties(Array.isArray(props) ? props : []);
            setPropsValid(!Array.isArray(props) || props.length === 0);
            setPropsLoading(false);
        }).catch(() => {
            setProperties([]);
            setPropsValid(true);
            setPropsLoading(false);
        });
    }, [selectedStage, open, pipelineId, deal.stage?.id]);

    const handleSave = () => {
        setSaving(true);
        setError(null);
        const updatedDeal = {
            ...deal,
            stage: stages.find((s) => s.id === selectedStage),
            ...(properties.length > 0 ? { properties } : {}),
        };
        api.CreateDealFromJson({ deal: updatedDeal }).then(() => {
            setSaving(false);
            onClose();
            onSuccess();
        }).catch(() => {
            setSaving(false);
            setError(t('errorMovingDeal'));
        });
    };

    const canSave = selectedStage !== deal.stage?.id && !propsLoading && propsValid;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="move-deal-title"
            PaperProps={{ sx: { ...DIALOG_PAPER, maxWidth: 480 } }}
        >
            <DialogTitle id="move-deal-title" sx={{ pb: 0.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <DriveFileMoveOutlinedIcon sx={{ color: ACCENT }} />
                    <Stack>
                        <span>{t('moveDeal')}</span>
                        <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY, fontWeight: 400 }}>
                            {deal.name}
                        </Typography>
                    </Stack>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ color: TEXT_SECONDARY, fontSize: TYPE_SCALE.sm, mb: 1.5, mt: 0.5 }}>
                    {t('selectStage')}
                </DialogContentText>
                {error && (
                    <Alert severity="error" sx={{ borderRadius: '12px', width: '100%', mb: 1.5 }}>
                        {error}
                    </Alert>
                )}

                {deal.stage?.label && (
                    <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_HINT, mb: 0.5 }}>
                        {t('currentStage')}: {deal.stage.label}
                    </Typography>
                )}

                <Stack
                    direction="row"
                    flexWrap="wrap"
                    gap={1}
                    sx={{ mb: 2 }}
                    className="hide-scrollbar"
                >
                    {stages.map((s) => {
                        const isSelected = selectedStage === s.id;
                        const isCurrent = deal.stage?.id === s.id;
                        const stageIdx = stages.findIndex(st => st.id === s.id);
                        const currentIdx = stages.findIndex(st => st.id === deal.stage?.id);
                        const isPast = stageIdx < currentIdx;
                        return (
                            <Chip
                                key={s.id}
                                label={s.label}
                                onClick={() => setSelectedStage(s.id)}
                                data-testid={`stage-chip-${s.id}`}
                                sx={{
                                    height: 40,
                                    borderRadius: '20px',
                                    fontWeight: isSelected ? 600 : 400,
                                    fontSize: TYPE_SCALE.sm,
                                    backgroundColor: isSelected ? ACCENT : isCurrent ? 'rgba(118,169,172,0.15)' : 'rgba(255,255,255,0.05)',
                                    color: isSelected ? '#0a1929' : isPast ? TEXT_HINT : TEXT_SECONDARY,
                                    border: isCurrent && !isSelected ? `1px solid ${ACCENT}40` : isSelected ? 'none' : `1px solid ${SURFACE_BORDER}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': { backgroundColor: isSelected ? ACCENT : SURFACE_HOVER },
                                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                                }}
                            />
                        );
                    })}
                </Stack>

                {propsLoading && (
                    <Stack alignItems="center" sx={{ py: 2 }}>
                        <CircularProgress size={24} sx={{ color: ACCENT, mb: 1 }} />
                        <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_HINT }}>
                            {t('loadingProperties')}
                        </Typography>
                    </Stack>
                )}

                {!propsLoading && properties.length > 0 && (
                    <Stack spacing={1.5}>
                        <Typography sx={{ fontSize: TYPE_SCALE.sm, color: TEXT_SECONDARY, fontWeight: 600 }}>
                            {t('stageProperties')}
                        </Typography>
                        <Properties
                            properties={properties}
                            setProperties={setProperties}
                            onValidationChange={setPropsValid}
                        />
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button
                    onClick={onClose}
                    disabled={saving}
                    sx={{
                        color: TEXT_SECONDARY,
                        fontWeight: 600,
                        borderRadius: '12px',
                        px: 2.5,
                        minHeight: 44,
                        '&:hover': { backgroundColor: SURFACE_HOVER },
                    }}
                >
                    {t('cancel')}
                </Button>
                <Button
                    onClick={handleSave}
                    disabled={saving || !canSave}
                    data-testid="move-deal-save"
                    sx={{
                        color: '#0a1929',
                        fontWeight: 600,
                        backgroundColor: ACCENT,
                        borderRadius: '12px',
                        px: 2.5,
                        minHeight: 44,
                        '&:hover': { backgroundColor: '#8bbfc2' },
                        '&:disabled': { backgroundColor: `${ACCENT}40`, color: 'rgba(255,255,255,0.3)' },
                    }}
                >
                    {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};


const DealCard = ({ deal, stages, reload, idx = 0, pipelineId }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [successMsg, setSuccessMsg] = useState(false);
    const navigate = useNavigate();
    const triggerRef = useRef(null);
    const urgency = getCloseDateUrgency(deal.closedate);
    const amountWeight = getDealAmountWeight(deal.amount);

    const handleClickOpen = () => setOpen(true);
    const handleClose = () => { setOpen(false); triggerRef.current?.focus(); };

    const handleCardClick = () => {
        navigate(`/app/deals/${deal.id}`, { state: { deal } });
    };

    const handleCardKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick();
        }
    };

    return (
        <>
            <Box
                onClick={handleCardClick}
                onKeyDown={handleCardKeyDown}
                tabIndex={0}
                role="button"
                data-testid={`deal-card-${deal.id}`}
                aria-label={`${deal.name}${deal.amount != null ? ` – ${currencyFormatter.format(deal.amount)}` : ''}`}
                sx={{
                    ...glassCard,
                    cursor: 'pointer',
                    transition: 'transform 0.15s ease, box-shadow 0.2s ease',
                    animation: 'fadeSlideIn 0.3s ease-out forwards',
                    animationDelay: `${Math.min(idx * 40, 400)}ms`,
                    opacity: 0,
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${ACCENT_GLOW}` },
                    '&:active': { transform: 'scale(0.96)' },
                    '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
                    '@media (prefers-reduced-motion: reduce)': {
                        transition: 'none',
                        animation: 'none',
                        opacity: 1,
                        '&:hover': { transform: 'none' },
                        '&:active': { transform: 'none' },
                    },
                }}
            >
                <Stack spacing={1.5}>
                    <Typography sx={{ fontWeight: 600, fontSize: TYPE_SCALE.md, color: TEXT_PRIMARY, lineHeight: 1.3 }} noWrap>
                        {deal.name}
                    </Typography>

                    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                        {deal.amount != null ? (
                            <Typography sx={{ ...amountWeight, color: ACCENT, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' }}>
                                {currencyFormatter.format(deal.amount)}
                            </Typography>
                        ) : <span />}
                        {deal.closedate && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                {urgency.urgency === 'overdue' && (
                                    <Chip
                                        label={t('overdue', 'Vencido')}
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontSize: TYPE_SCALE.xs,
                                            fontWeight: 700,
                                            borderRadius: '6px',
                                            backgroundColor: 'rgba(229,115,115,0.15)',
                                            color: STATUS_LOST,
                                            border: 'none',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                        }}
                                    />
                                )}
                                {urgency.urgency === 'soon' && (
                                    <Chip
                                        label={t('closingSoon', 'Pronto')}
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontSize: TYPE_SCALE.xs,
                                            fontWeight: 700,
                                            borderRadius: '6px',
                                            backgroundColor: 'rgba(255,183,77,0.15)',
                                            color: '#FFB74D',
                                            border: 'none',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                        }}
                                    />
                                )}
                                <Typography sx={{ fontSize: TYPE_SCALE.xs, color: urgency.color }}>
                                    {dayjs(deal.closedate).format('DD/MM/YYYY')}
                                </Typography>
                            </Stack>
                        )}
                    </Stack>

                    {deal.stage?.label && (
                        <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY, fontWeight: 500 }}>
                            {deal.stage.label}
                        </Typography>
                    )}

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        {deal.status && (
                            <Chip size="small" label={deal.status} sx={{
                                height: 26, fontSize: TYPE_SCALE.xs, fontWeight: 500,
                                backgroundColor: 'rgba(118,169,172,0.12)', color: TEXT_SECONDARY,
                                border: 'none',
                            }} />
                        )}
                        <Button
                            ref={triggerRef}
                            size="small"
                            startIcon={<DriveFileMoveOutlinedIcon fontSize="small" />}
                            onClick={(e) => { e.stopPropagation(); handleClickOpen(); }}
                            onKeyDown={(e) => e.stopPropagation()}
                            data-testid={`move-deal-${deal.id}`}
                            sx={{
                                color: ACCENT,
                                borderRadius: '22px',
                                border: '1px solid rgba(118,169,172,0.2)',
                                textTransform: 'none',
                                fontSize: TYPE_SCALE.xs,
                                fontWeight: 600,
                                minHeight: '44px',
                                px: 2,
                                ml: 'auto',
                                '&:hover': { backgroundColor: 'rgba(118,169,172,0.1)', borderColor: ACCENT },
                            }}
                        >
                            {t('moveDeal')}
                        </Button>
                    </Stack>
                </Stack>
            </Box>

            <MoveDealDialog
                open={open}
                onClose={handleClose}
                deal={deal}
                stages={stages}
                pipelineId={pipelineId}
                onSuccess={() => { setSuccessMsg(true); reload(); }}
            />

            <Snackbar
                open={successMsg}
                autoHideDuration={3000}
                onClose={() => setSuccessMsg(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setSuccessMsg(false)} sx={{ borderRadius: '12px' }}>
                    {t('dealMoved', 'Deal moved successfully')}
                </Alert>
            </Snackbar>
        </>
    );
};


const KanbanDealCard = ({ deal, stages, currentStageIdx, reload, pipelineId }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [successMsg, setSuccessMsg] = useState(false);
    const urgency = getCloseDateUrgency(deal.closedate);

    return (
        <>
            <Box
                onClick={() => navigate(`/app/deals/${deal.id}`, { state: { deal } })}
                tabIndex={0}
                role="button"
                aria-label={`${deal.name}${deal.amount != null ? ` – ${currencyFormatter.format(deal.amount)}` : ''}`}
                sx={{
                    ...glassCard,
                    p: 1.5,
                    cursor: 'pointer',
                    transition: 'transform 0.15s ease, box-shadow 0.2s ease',
                    '&:active': { transform: 'scale(0.96)' },
                    '&:hover': { boxShadow: `0 4px 16px ${ACCENT_GLOW}` },
                    '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
                    ...noMotion,
                }}
            >
                <Stack spacing={0.75}>
                    <Typography sx={{ fontWeight: 600, fontSize: TYPE_SCALE.sm, color: TEXT_PRIMARY, lineHeight: 1.3 }} noWrap>
                        {deal.name}
                    </Typography>
                    {deal.amount != null && (
                        <Typography sx={{ fontWeight: 700, fontSize: TYPE_SCALE.lg, color: ACCENT, fontVariantNumeric: 'tabular-nums' }}>
                            {currencyFormatter.format(deal.amount)}
                        </Typography>
                    )}
                    {deal.closedate && (
                        <Typography sx={{ fontSize: TYPE_SCALE.xs, color: urgency.color }}>
                            {dayjs(deal.closedate).format('DD/MM')}
                        </Typography>
                    )}
                    <Button
                        size="small"
                        startIcon={<DriveFileMoveOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                        data-testid={`kanban-move-${deal.id}`}
                        sx={{
                            alignSelf: 'flex-end',
                            color: ACCENT,
                            fontSize: TYPE_SCALE.xs,
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '12px',
                            border: `1px solid ${ACCENT}30`,
                            minHeight: 36,
                            px: 1.5,
                            '&:hover': { backgroundColor: `${ACCENT}15` },
                        }}
                    >
                        {t('moveDeal')}
                    </Button>
                </Stack>
            </Box>

            <MoveDealDialog
                open={open}
                onClose={() => setOpen(false)}
                deal={deal}
                stages={stages}
                pipelineId={pipelineId}
                onSuccess={() => { setSuccessMsg(true); reload(); }}
            />

            <Snackbar
                open={successMsg}
                autoHideDuration={3000}
                onClose={() => setSuccessMsg(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="success" onClose={() => setSuccessMsg(false)} sx={{ borderRadius: '12px' }}>
                    {t('dealMoved', 'Deal moved successfully')}
                </Alert>
            </Snackbar>
        </>
    );
};


const KanbanView = ({ deals, stages, pipeline, reload }) => {
    const { t } = useTranslation();
    const columnRefs = useRef({});
    const [activeColumn, setActiveColumn] = useState(stages[0]?.id ?? null);

    const allDealsInPipeline = useMemo(() => {
        const pipelineData = deals.find(d => d?.pipeline?.id === pipeline);
        return pipelineData?.deals || [];
    }, [deals, pipeline]);

    useEffect(() => {
        const observers = [];
        const currentRefs = columnRefs.current;
        stages.forEach((stg) => {
            const el = currentRefs[stg.id];
            if (!el) return;
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) setActiveColumn(stg.id);
                },
                { threshold: 0.5, root: el.parentElement }
            );
            observer.observe(el);
            observers.push(observer);
        });
        return () => observers.forEach(o => o.disconnect());
    }, [stages, pipeline]);

    const scrollToColumn = useCallback((stageId) => {
        const el = columnRefs.current[stageId];
        if (el) el.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }, []);

    return (
        <>
            <Stack
                direction="row"
                spacing={1}
                className="hide-scrollbar"
                sx={{
                    overflowX: 'auto',
                    py: 1.5,
                    scrollSnapType: 'x proximity',
                    WebkitOverflowScrolling: 'touch',
                    maskImage: stages.length > 4
                        ? 'linear-gradient(to right, black calc(100% - 40px), transparent 100%)'
                        : 'none',
                    WebkitMaskImage: stages.length > 4
                        ? 'linear-gradient(to right, black calc(100% - 40px), transparent 100%)'
                        : 'none',
                }}
            >
                {stages.map((s) => (
                    <Chip
                        key={s.id}
                        label={s.label}
                        onClick={() => scrollToColumn(s.id)}
                        data-testid={`kanban-chip-${s.id}`}
                        sx={{
                            scrollSnapAlign: 'start',
                            flexShrink: 0,
                            height: 44,
                            borderRadius: '22px',
                            fontWeight: activeColumn === s.id ? 600 : 400,
                            fontSize: TYPE_SCALE.sm,
                            backgroundColor: activeColumn === s.id ? ACCENT : 'rgba(255,255,255,0.05)',
                            color: activeColumn === s.id ? '#0a1929' : TEXT_SECONDARY,
                            border: activeColumn === s.id ? 'none' : `1px solid ${SURFACE_BORDER}`,
                            transition: 'all 0.2s ease',
                            '&:hover': { backgroundColor: activeColumn === s.id ? ACCENT : SURFACE_HOVER },
                            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                        }}
                    />
                ))}
            </Stack>

            <Box
                className="hide-scrollbar"
                data-testid="kanban-container"
                sx={{
                    display: 'flex',
                    gap: 1.5,
                    overflowX: 'auto',
                    pb: 2,
                    mt: 1,
                    scrollSnapType: 'x mandatory',
                    WebkitOverflowScrolling: 'touch',
                }}
            >
                {stages.map((stg, colIdx) => {
                    const stageDeals = allDealsInPipeline.filter(d => d.stage?.id === stg.id);
                    const totalAmount = stageDeals.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
                    return (
                        <Stack
                            key={stg.id}
                            ref={el => columnRefs.current[stg.id] = el}
                            sx={{
                                minWidth: 260,
                                flex: '0 0 260px',
                                scrollSnapAlign: 'start',
                                backgroundColor: 'rgba(255,255,255,0.02)',
                                borderRadius: '12px',
                                border: `1px solid ${SURFACE_BORDER}`,
                                p: 1.5,
                            }}
                        >
                            <Stack sx={{ mb: 1.5 }}>
                                <Typography sx={{ fontSize: TYPE_SCALE.sm, fontWeight: 600, color: TEXT_PRIMARY }}>
                                    {stg.label}
                                </Typography>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_HINT }}>
                                        {stageDeals.length}
                                    </Typography>
                                    <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_HINT }}>·</Typography>
                                    <Typography sx={{ fontSize: TYPE_SCALE.xs, color: ACCENT, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                        {currencyFormatter.format(totalAmount)}
                                    </Typography>
                                </Stack>
                            </Stack>
                            <Stack spacing={1}>
                                {stageDeals.map(d => (
                                    <KanbanDealCard
                                        key={d.id}
                                        deal={d}
                                        stages={stages}
                                        currentStageIdx={colIdx}
                                        reload={reload}
                                        pipelineId={pipeline}
                                    />
                                ))}
                                {stageDeals.length === 0 && (
                                    <Typography sx={{ textAlign: 'center', py: 4, fontSize: TYPE_SCALE.xs, color: TEXT_HINT }}>
                                        {t('noDealsInStage', '—')}
                                    </Typography>
                                )}
                            </Stack>
                        </Stack>
                    );
                })}
            </Box>
        </>
    );
};


const Deals = () => {
    const { state } = useLocation();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const crmUpdated = state?.ok ?? false;
    const [filteredDeals, setFilteredDeals] = useState([]);
    const [deals, setDeals] = useState([]);
    const [pipeline, setPipeline] = useState(undefined);
    const [pipelines, setPipelines] = useState([]);
    const [stage, setStage] = useState(-1);
    const [stages, setStages] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [initialLoading, setInitialLoading] = useState(true);
    const [closedate, setClosedate] = useState("DESCENDING");
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [viewMode, setViewMode] = useState('LIST');
    const previousController = useRef();
    const stageScrollRef = useRef(null);
    const debounceRef = useRef(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const navigate = useNavigate();
    const initialUrlPipeline = useRef(searchParams.get('pipeline'));
    const initialUrlStage = useRef(searchParams.get('stage'));

    useEffect(() => {
        fetchDeals(undefined, undefined, true);
        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, []);

    useEffect(() => {
        if (stageScrollRef.current && stage !== -1) {
            requestAnimationFrame(() => {
                const activeChip = stageScrollRef.current?.querySelector('[aria-selected="true"]');
                if (activeChip && stageScrollRef.current) {
                    const container = stageScrollRef.current;
                    const chipLeft = activeChip.offsetLeft;
                    const chipWidth = activeChip.offsetWidth;
                    const containerWidth = container.offsetWidth;
                    container.scrollLeft = chipLeft - (containerWidth / 2) + (chipWidth / 2);
                }
            });
        }
    }, [stage]);

    const handleSearchChange = (text) => {
        setSearchTerm(text);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPipeline(undefined);
            setStage(-1);
            fetchDeals(text, false);
        }, 300);
    };

    const handleChangePipeline = (event) => {
        const newPipeline = event.target.value;
        const pipelineData = deals.find((option) => option?.pipeline?.id === newPipeline);
        if (!pipelineData) return;
        const newStage = pipelineData.stages[0].id;
        filterDeals(newPipeline, newStage, deals);
        setDrawerOpen(false);
    };

    const handleChangeStage = (event, newStage) => {
        setStage(newStage);
        filterDeals(pipeline, newStage, deals);
    };

    const handleSortChange = (term, createdate) => {
        setClosedate(createdate);
        fetchDeals(term, true, false, createdate);
    };

    const fetchDeals = (search, filterPipeline = true, noCache = false, sortOrder) => {
        if (previousController.current) {
            previousController.current.abort();
        }
        const controller = new AbortController();
        const signal = controller.signal;
        previousController.current = controller;

        const options = {
            after: 0,
            closedate: sortOrder !== undefined ? sortOrder : closedate,
            signal,
            noCache,
        };
        if ((search && search.length > 0) || (searchTerm && searchTerm.length > 0)) {
            options.value = search !== undefined ? search : searchTerm;
        }

        api.fetchDealsGrouped(options).then((response) => {
            if (response.options.length > 0) {
                const firstPipelineId = response.options[0].pipeline.id;

                let targetPipeline = initialUrlPipeline.current
                    || (filterPipeline ? pipeline : null)
                    || firstPipelineId;
                initialUrlPipeline.current = null;

                if (!response.options.find(o => o.pipeline.id === targetPipeline)) {
                    targetPipeline = firstPipelineId;
                }

                const matchedPipeline = response.options.find(o => o.pipeline.id === targetPipeline);
                const pStages = matchedPipeline.stages;

                let targetStage = initialUrlStage.current;
                initialUrlStage.current = null;
                if (!targetStage || !pStages.find(s => s.id === targetStage)) {
                    const stageObj = (search && search !== "" || search === undefined) && matchedPipeline.deals.length > 0
                        ? matchedPipeline.deals[0].stage
                        : pStages[0];
                    targetStage = stageObj.id;
                }

                setDeals(response.options);
                setStages(pStages);
                setPipelines(response.options.map(o => o.pipeline));
                filterDeals(targetPipeline, targetStage, response.options);
            } else {
                setFilteredDeals([]);
            }
            setInitialLoading(false);
        }).catch((error) => {
            if (error?.name !== 'CanceledError' && error?.code !== 'ERR_CANCELED') {
                setInitialLoading(false);
                console.error("Error fetching deals", error);
            }
        });
    };

    const filterDeals = (pipelineId, stageId, dealsData) => {
        setStage(stageId);
        setPipeline(pipelineId);
        setSearchParams({ pipeline: pipelineId, stage: stageId }, { replace: true });
        const pipelineData = dealsData.find((option) => option?.pipeline?.id === pipelineId);
        if (!pipelineData) {
            setFilteredDeals([]);
            return;
        }
        setFilteredDeals(pipelineData.deals.filter((deal) => deal.stage?.id === stageId));
    };

    const reloadDeals = () => fetchDeals(undefined, true, true);

    return (
        <Stack sx={{ p: 2, width: '100%', maxWidth: 1200, mx: 'auto' }}>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
                <IconButton
                    aria-label={t('goBack', 'Go back')}
                    data-testid="deals-back"
                    sx={{ color: 'white' }}
                    onClick={() => navigate("/app")}
                >
                    <ArrowBack />
                </IconButton>
                <Typography variant="h5" sx={{ color: 'white', textAlign: 'left', flex: 1 }}>
                    {t('deals')}
                </Typography>
            </Stack>

            <Snackbar open={crmUpdated} autoHideDuration={4000} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert icon={<CheckIcon style={{ color: "white" }} fontSize="inherit" />} sx={{ borderRadius: '12px', backgroundColor: '#366E6B', color: 'white' }}>
                    {t('crmUpdated')}
                </Alert>
            </Snackbar>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                <SearchComponent
                    createdate={closedate}
                    handleSortChange={handleSortChange}
                    handleSearchChange={handleSearchChange}
                />
                <div style={{ display: pipelines.length > 1 ? 'block' : 'none' }}>
                    <IconButton
                        aria-label={t('filterPipeline', 'Filter by pipeline')}
                        data-testid="pipeline-filter"
                        sx={{
                            backgroundColor: drawerOpen ? 'rgba(255,255,255,0.12)' : 'transparent',
                            color: 'white',
                        }}
                        onClick={(event) => {
                            setAnchorEl(event.currentTarget);
                            setDrawerOpen(!drawerOpen);
                        }}
                    >
                        <FilterAltOutlined />
                    </IconButton>
                    <Popper
                        placement="bottom-end"
                        open={drawerOpen}
                        disablePortal={false}
                        anchorEl={anchorEl}
                        modifiers={[
                            { name: 'flip', enabled: true, options: { altBoundary: true, rootBoundary: 'document', padding: 8 } },
                            { name: 'preventOverflow', enabled: true, options: { altAxis: true, altBoundary: true, tether: true, rootBoundary: 'document', padding: 8 } },
                            { name: 'arrow', enabled: false },
                        ]}
                    >
                        <ClickAwayListener onClickAway={() => setDrawerOpen(false)}>
                            <Stack direction="column" spacing={2} sx={{
                                backgroundColor: 'rgba(30,35,50,0.98)',
                                backdropFilter: 'blur(16px)',
                                border: `1px solid ${SURFACE_BORDER}`,
                                p: 2.5, borderRadius: '16px', mt: 1.25, width: 280,
                            }}>
                                <Typography sx={{ color: TEXT_SECONDARY, fontSize: TYPE_SCALE.lg }}>
                                    Pipeline
                                </Typography>
                                <RadioGroup value={pipeline} onChange={handleChangePipeline}>
                                    {pipelines.map((p) => (
                                        <FormControlLabel
                                            key={p.id}
                                            value={p.id}
                                            label={p.label}
                                            control={<Radio sx={{ color: TEXT_HINT, '&.Mui-checked': { color: ACCENT } }} />}
                                            sx={{ color: TEXT_PRIMARY, minHeight: 48 }}
                                        />
                                    ))}
                                </RadioGroup>
                            </Stack>
                        </ClickAwayListener>
                    </Popper>
                </div>
            </Stack>

            <Stack direction="row" alignItems="center" sx={{ py: 1 }}>
                <Stack direction="row" spacing={0.5} sx={{ backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: '12px', p: 0.5 }}>
                    <Chip
                        icon={<ViewListIcon sx={{ fontSize: 18 }} />}
                        label={t('list', 'Lista')}
                        data-testid="view-list"
                        onClick={() => setViewMode('LIST')}
                        sx={{
                            height: 36, borderRadius: '10px', fontSize: TYPE_SCALE.xs, fontWeight: viewMode === 'LIST' ? 600 : 400,
                            backgroundColor: viewMode === 'LIST' ? ACCENT : 'transparent',
                            color: viewMode === 'LIST' ? '#0a1929' : TEXT_TERTIARY,
                            '& .MuiChip-icon': { color: viewMode === 'LIST' ? '#0a1929' : TEXT_TERTIARY },
                            '&:hover': { backgroundColor: viewMode === 'LIST' ? ACCENT : SURFACE_HOVER },
                        }}
                    />
                    <Chip
                        icon={<ViewColumnIcon sx={{ fontSize: 18 }} />}
                        label={t('kanban', 'Kanban')}
                        data-testid="view-kanban"
                        onClick={() => setViewMode('KANBAN')}
                        sx={{
                            height: 36, borderRadius: '10px', fontSize: TYPE_SCALE.xs, fontWeight: viewMode === 'KANBAN' ? 600 : 400,
                            backgroundColor: viewMode === 'KANBAN' ? ACCENT : 'transparent',
                            color: viewMode === 'KANBAN' ? '#0a1929' : TEXT_TERTIARY,
                            '& .MuiChip-icon': { color: viewMode === 'KANBAN' ? '#0a1929' : TEXT_TERTIARY },
                            '&:hover': { backgroundColor: viewMode === 'KANBAN' ? ACCENT : SURFACE_HOVER },
                        }}
                    />
                </Stack>
            </Stack>

            {viewMode === 'LIST' && (
                initialLoading ? <StageChipsSkeleton /> : (
                    <Stack
                        ref={stageScrollRef}
                        direction="row"
                        spacing={1}
                        role="tablist"
                        aria-label={t('pipelineStages', 'Pipeline stages')}
                        className="hide-scrollbar"
                        sx={{
                            overflowX: 'auto',
                            py: 1.5,
                            scrollSnapType: 'x proximity',
                            WebkitOverflowScrolling: 'touch',
                            maskImage: stages.length > 4
                                ? 'linear-gradient(to right, black calc(100% - 40px), transparent 100%)'
                                : 'none',
                            WebkitMaskImage: stages.length > 4
                                ? 'linear-gradient(to right, black calc(100% - 40px), transparent 100%)'
                                : 'none',
                        }}
                    >
                        {stages.map((s) => (
                            <Chip
                                key={s.id}
                                role="tab"
                                aria-selected={stage === s.id}
                                label={s.label}
                                onClick={() => handleChangeStage(null, s.id)}
                                sx={{
                                    scrollSnapAlign: 'start',
                                    flexShrink: 0,
                                    height: 44,
                                    borderRadius: '22px',
                                    fontWeight: stage === s.id ? 600 : 400,
                                    fontSize: TYPE_SCALE.sm,
                                    backgroundColor: stage === s.id ? ACCENT : 'rgba(255,255,255,0.05)',
                                    color: stage === s.id ? '#0a1929' : TEXT_SECONDARY,
                                    border: stage === s.id ? 'none' : `1px solid ${SURFACE_BORDER}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': { backgroundColor: stage === s.id ? ACCENT : SURFACE_HOVER },
                                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                                }}
                            />
                        ))}
                    </Stack>
                )
            )}

            <div aria-live="polite" aria-atomic="false">
                {!initialLoading && viewMode === 'LIST' && filteredDeals.length > 0 && (
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ pb: 1.5, px: 0.5, flexShrink: 0 }}>
                        <Typography variant="caption" sx={{ color: TEXT_HINT, fontSize: TYPE_SCALE.xs, whiteSpace: 'nowrap' }}>
                            {filteredDeals.length} {t('deals').toLowerCase()}
                        </Typography>
                        <Typography variant="caption" sx={{ color: TEXT_HINT }}>·</Typography>
                        <Typography variant="caption" sx={{ color: ACCENT, fontWeight: 600, fontSize: TYPE_SCALE.xs, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                            {currencyFormatter.format(filteredDeals.reduce((sum, d) => sum + (Number(d.amount) || 0), 0))}
                        </Typography>
                    </Stack>
                )}

                {!initialLoading && viewMode === 'LIST' && filteredDeals.length === 0 && (
                    <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 8, color: TEXT_HINT }}>
                        <InboxIcon sx={{ fontSize: 48 }} />
                        <Typography variant="body1">{t('noDealsFound', 'No deals found')}</Typography>
                    </Stack>
                )}
            </div>

            {viewMode === 'LIST' && (
                initialLoading ? (
                    <Grid container spacing={1}>
                        {[0, 1, 2, 3].map(i => (
                            <Grid key={i} item xs={12} sm={6} md={4} lg={3}>
                                <DealCardSkeleton />
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Grid data-testid="list-container" key={`${pipeline}-${stage}`} container spacing={1}>
                        {filteredDeals.map((deal, idx) => (
                            <Grid data-testid={`record-card-${idx}`} key={deal.id || idx} item xs={12} sm={6} md={4} lg={3}>
                                <DealCard deal={deal} stages={stages} reload={reloadDeals} idx={idx} pipelineId={pipeline} />
                            </Grid>
                        ))}
                    </Grid>
                )
            )}

            {viewMode === 'KANBAN' && !initialLoading && (
                <KanbanView
                    deals={deals}
                    stages={stages}
                    pipeline={pipeline}
                    reload={reloadDeals}
                />
            )}
        </Stack>
    );
};


const StageProgressBar = ({ stages, currentStageId }) => {
    const currentIdx = stages.findIndex(s => s.id === currentStageId);
    return (
        <Box data-testid="stage-progress-bar" sx={{ width: '100%', overflowX: 'auto', py: 1 }} className="hide-scrollbar">
            <Stack direction="row" alignItems="center" sx={{ minWidth: 'max-content', px: 0.5 }}>
                {stages.map((stage, idx) => {
                    const isCompleted = idx < currentIdx;
                    const isCurrent = idx === currentIdx;
                    const isFuture = idx > currentIdx;
                    return (
                        <React.Fragment key={stage.id}>
                            {idx > 0 && (
                                <Box sx={{
                                    flex: 1, minWidth: 16, height: 2,
                                    bgcolor: isCompleted || isCurrent ? ACCENT : SURFACE_BORDER,
                                    transition: 'background-color 200ms ease-out',
                                    ...noMotion
                                }} />
                            )}
                            <Stack alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
                                <Box sx={{
                                    width: isCurrent ? 14 : 10,
                                    height: isCurrent ? 14 : 10,
                                    borderRadius: '50%',
                                    bgcolor: isFuture ? SURFACE_BORDER : ACCENT,
                                    boxShadow: isCurrent ? `0 0 8px 3px ${ACCENT_GLOW}` : 'none',
                                    transition: 'all 200ms ease-out',
                                    flexShrink: 0,
                                    ...noMotion
                                }} />
                                <Typography sx={{
                                    fontSize: TYPE_SCALE.xs,
                                    color: isCurrent ? TEXT_PRIMARY : TEXT_HINT,
                                    fontWeight: isCurrent ? 600 : 400,
                                    whiteSpace: 'nowrap',
                                    display: { xs: isCurrent ? 'block' : 'none', sm: 'block' }
                                }}>
                                    {stage.label}
                                </Typography>
                            </Stack>
                        </React.Fragment>
                    );
                })}
            </Stack>
        </Box>
    );
};

const DetailedInfoDeal = () => {
    const { t } = useTranslation();
    const { dealId } = useParams();
    const { state } = useLocation();
    const navigate = useNavigate();
    const [deal, setDeal] = useState(state?.deal ?? null);
    const [loading, setLoading] = useState(true);
    const [moveDialogOpen, setMoveDialogOpen] = useState(false);
    const [successMsg, setSuccessMsg] = useState(false);

    const fetchDeal = useCallback(() => {
        api.fetchDeal(dealId).then((response) => {
            setDeal(prev => prev ? { ...prev, ...response } : response);
            setLoading(false);
        }).catch(() => {
            if (!deal) navigate('/app/deals', { replace: true });
            setLoading(false);
        });
    }, [dealId]);

    useEffect(() => {
        if (!dealId) {
            navigate('/app/deals', { replace: true });
            return;
        }
        fetchDeal();
    }, [dealId, fetchDeal]);

    if (!deal && !loading) return null;

    // Loading skeleton
    if (!deal && loading) {
        return (
            <Stack sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ bgcolor: SURFACE_HOVER }} />
                    <Skeleton variant="text" width="50%" sx={{ bgcolor: SURFACE_HOVER, fontSize: TYPE_SCALE['2xl'] }} />
                </Stack>
                <Skeleton variant="text" width="40%" sx={{ bgcolor: SURFACE_HOVER, fontSize: '1.8rem', mb: 1 }} />
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Skeleton variant="rounded" width={100} height={28} sx={{ borderRadius: '14px', bgcolor: SURFACE_HOVER }} />
                    <Skeleton variant="text" width="30%" sx={{ bgcolor: SURFACE_HOVER }} />
                </Stack>
                <Skeleton variant="rounded" width="100%" height={20} sx={{ borderRadius: '10px', bgcolor: SURFACE_HOVER, mb: 3 }} />
                <Skeleton variant="rounded" width="100%" height={120} sx={{ borderRadius: '16px', bgcolor: SURFACE_HOVER, mb: 2 }} />
                <Skeleton variant="rounded" width="100%" height={100} sx={{ borderRadius: '16px', bgcolor: SURFACE_HOVER, mb: 2 }} />
                <Skeleton variant="rounded" width="100%" height={48} sx={{ borderRadius: '14px', bgcolor: SURFACE_HOVER }} />
            </Stack>
        );
    }

    const urgency = deal.closedate ? getCloseDateUrgency(deal.closedate) : { color: TEXT_TERTIARY, urgency: 'none' };
    const contact = deal.contacts?.length > 0 ? deal.contacts[0] : null;

    return (
        <Stack sx={{ p: { xs: 2, sm: 3 } }} spacing={2.5}>
            {/* Header */}
            <Stack direction="row" spacing={1.5} alignItems="center">
                <IconButton
                    data-testid="deal-detail-back"
                    aria-label={t('goBack', 'Go back')}
                    onClick={() => navigate('/app/deals')}
                    sx={{ color: TEXT_SECONDARY }}
                >
                    <ArrowBack />
                </IconButton>
                <Typography sx={{ fontSize: TYPE_SCALE['2xl'], fontWeight: 700, color: TEXT_PRIMARY, lineHeight: 1.2 }}>
                    {deal.name}
                </Typography>
            </Stack>

            {/* Hero — amount, stage chip, pipeline */}
            <Stack spacing={1}>
                {deal.amount != null && (
                    <Typography sx={{ fontSize: '1.8rem', fontWeight: 800, color: ACCENT, lineHeight: 1.1 }}>
                        {currencyFormatter.format(deal.amount)}
                    </Typography>
                )}
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    {deal.stage?.label && (
                        <Chip
                            data-testid="deal-stage-badge"
                            label={deal.stage.label}
                            size="small"
                            sx={{
                                bgcolor: `${ACCENT}20`,
                                color: ACCENT,
                                fontWeight: 600,
                                fontSize: TYPE_SCALE.sm,
                                height: 28,
                                borderRadius: '14px'
                            }}
                        />
                    )}
                    {deal.pipeline?.label && (
                        <Typography sx={{ fontSize: TYPE_SCALE.sm, color: TEXT_TERTIARY }}>
                            {deal.pipeline.label}
                        </Typography>
                    )}
                </Stack>
            </Stack>

            {/* Stage Progress Bar */}
            {deal.allStages?.length > 0 && (
                <StageProgressBar stages={deal.allStages} currentStageId={deal.stage?.id} />
            )}

            {/* Urgency Alert */}
            {urgency.urgency === 'overdue' && (
                <Box
                    aria-live="polite"
                    sx={{
                        ...glassCard,
                        bgcolor: 'rgba(229,115,115,0.08)',
                        border: `1px solid rgba(229,115,115,0.2)`,
                        py: 1.5
                    }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <CalendarMonthIcon sx={{ color: STATUS_LOST, fontSize: 20 }} />
                        <Stack>
                            <Typography sx={{ fontSize: TYPE_SCALE.sm, fontWeight: 600, color: STATUS_LOST }}>
                                {t('overdue')}
                            </Typography>
                            <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY }}>
                                {dayjs(deal.closedate).fromNow()}
                            </Typography>
                        </Stack>
                    </Stack>
                </Box>
            )}

            {/* Deal Info Card */}
            <Box data-testid="deal-info-card" sx={{ ...glassCard }}>
                <Stack spacing={2}>
                    {/* Close date row */}
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{
                            width: 44, height: 44, borderRadius: '12px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: `${urgency.color}15`
                        }}>
                            <CalendarMonthIcon sx={{ color: urgency.color, fontSize: 22 }} />
                        </Box>
                        <Stack>
                            <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {t('closedate')}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography sx={{ fontSize: TYPE_SCALE.lg, fontWeight: 600, color: TEXT_PRIMARY }}>
                                    {deal.closedate ? dayjs(deal.closedate).format('L') : '—'}
                                </Typography>
                                {urgency.urgency === 'soon' && (
                                    <Chip
                                        label={t('closingSoon')}
                                        size="small"
                                        sx={{
                                            height: 22, fontSize: TYPE_SCALE.xs, fontWeight: 600,
                                            bgcolor: 'rgba(255,183,77,0.12)', color: '#FFB74D',
                                            borderRadius: '11px'
                                        }}
                                    />
                                )}
                            </Stack>
                        </Stack>
                    </Stack>

                    <Divider sx={{ borderColor: SURFACE_BORDER }} />

                    {/* Create date row */}
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{
                            width: 44, height: 44, borderRadius: '12px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: `${ACCENT}15`
                        }}>
                            <CalendarMonthIcon sx={{ color: ACCENT, fontSize: 22 }} />
                        </Box>
                        <Stack>
                            <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {t('createdate')}
                            </Typography>
                            <Typography sx={{ fontSize: TYPE_SCALE.lg, fontWeight: 600, color: TEXT_PRIMARY }}>
                                {deal.createdate ? dayjs(deal.createdate).format('L') : '—'}
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
            </Box>

            {/* Associated Contact Card */}
            {contact && (
                <Box
                    data-testid="deal-contact-card"
                    role="button"
                    tabIndex={0}
                    aria-label={`${t('contact')}: ${getContactName(contact)}`}
                    onClick={() => navigate('/app/contacts/detailed', { state: { contact } })}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/app/contacts/detailed', { state: { contact } }); } }}
                    sx={{
                        ...glassCard,
                        cursor: 'pointer',
                        transition: 'background-color 200ms ease-out, border-color 200ms ease-out',
                        '&:hover': { bgcolor: SURFACE_HOVER, borderColor: `${ACCENT}30` },
                        '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
                        ...noMotion
                    }}
                >
                    <Stack spacing={1.5}>
                        <Typography sx={{ fontSize: TYPE_SCALE.xs, color: TEXT_TERTIARY, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {t('contact')}
                        </Typography>
                        <Typography sx={{ fontSize: TYPE_SCALE.lg, fontWeight: 600, color: TEXT_PRIMARY }}>
                            {getContactName(contact)}
                        </Typography>
                        {contact.email && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <MailOutlined sx={{ fontSize: 16, color: TEXT_HINT }} />
                                <Typography sx={{ fontSize: TYPE_SCALE.sm, color: TEXT_SECONDARY }}>{contact.email}</Typography>
                            </Stack>
                        )}
                        {contact.phone && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <PhoneOutlined sx={{ fontSize: 16, color: TEXT_HINT }} />
                                <Typography sx={{ fontSize: TYPE_SCALE.sm, color: TEXT_SECONDARY }}>{contact.phone}</Typography>
                            </Stack>
                        )}
                        {contact.company && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <WorkOutlineOutlined sx={{ fontSize: 16, color: TEXT_HINT }} />
                                <Typography sx={{ fontSize: TYPE_SCALE.sm, color: TEXT_SECONDARY }}>
                                    {typeof contact.company === 'string' ? contact.company : (contact.company.name || contact.company.domain || '—')}
                                </Typography>
                            </Stack>
                        )}
                        <Stack direction="row" justifyContent="flex-end">
                            <ChevronRightIcon sx={{ color: TEXT_HINT, fontSize: 20 }} />
                        </Stack>
                    </Stack>
                </Box>
            )}

            {/* Action Buttons */}
            <Stack spacing={1.5}>
                {deal.allStages?.length > 0 && (
                    <Button
                        data-testid="deal-move-stage"
                        aria-label={t('moveDeal')}
                        variant="contained"
                        startIcon={<DriveFileMoveOutlinedIcon />}
                        onClick={() => setMoveDialogOpen(true)}
                        sx={{
                            bgcolor: ACCENT, color: '#0a1929', fontWeight: 600,
                            borderRadius: '14px', height: 48, textTransform: 'none',
                            fontSize: TYPE_SCALE.md,
                            '&:hover': { bgcolor: ACCENT_DARK },
                            ...noMotion
                        }}
                    >
                        {t('moveDeal')}
                    </Button>
                )}
            </Stack>

            {/* Move Deal Dialog */}
            {deal.allStages?.length > 0 && deal.stage && (
                <MoveDealDialog
                    open={moveDialogOpen}
                    onClose={() => setMoveDialogOpen(false)}
                    deal={deal}
                    stages={deal.allStages}
                    pipelineId={deal.pipeline?.id}
                    onSuccess={() => {
                        setSuccessMsg(true);
                        fetchDeal();
                    }}
                />
            )}

            {/* Success Snackbar */}
            <Snackbar
                open={successMsg}
                autoHideDuration={3000}
                onClose={() => setSuccessMsg(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="success" sx={{ borderRadius: '12px' }}>
                    {t('dealMoved')}
                </Alert>
            </Snackbar>
        </Stack>
    );
};

export default {
    Deals,
    DealCard,
    DetailedInfoDeal
};
